// src/ui/Sheet.jsx
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Button from "./Button.jsx";
import { PageTitle } from "./Typography.jsx";

const SIDE_STYLES = {
  right: "top-0 right-0 h-full w-[min(520px,100%)] rounded-l-2xl",
  bottom: "left-0 right-0 bottom-0 w-full max-h-[90vh] rounded-t-2xl",
};

export default function Sheet({ open, onClose, title, side = "right", children }) {
  const [viewportSide, setViewportSide] = useState(side);

  useEffect(() => {
    if (side !== "auto") {
      setViewportSide(side);
      return;
    }
    const decide = () => {
      const isMobile = window.matchMedia("(max-width: 768px)").matches;
      setViewportSide(isMobile ? "bottom" : "right");
    };
    decide();
    window.addEventListener("resize", decide);
    return () => window.removeEventListener("resize", decide);
  }, [side]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const sideClass = SIDE_STYLES[viewportSide] || SIDE_STYLES.right;
  const panelStyle = {
    background: "color-mix(in_lab,var(--mudo-bg-soft),transparent 15%)",
    color: "var(--mudo-text)",
    borderColor: "var(--mudo-border)",
    backdropFilter: "blur(18px)",
  };

  const contentMaxHeight =
    viewportSide === "bottom" ? "70vh" : "calc(100vh - 80px)";

  return createPortal(
    <div className="fixed inset-0 z-[120]" aria-modal="true" role="dialog">
      <button
        type="button"
        aria-label="Close panel"
        className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        className={`absolute border shadow-2xl flex flex-col ${sideClass}`}
        style={panelStyle}
      >
        {(title || onClose) && (
          <div
            className="flex items-center justify-between gap-3 p-4 border-b"
            style={{ borderColor: "var(--mudo-border)" }}
          >
            {title ? <PageTitle className="text-lg">{title}</PageTitle> : <span />}
            <Button variant="ghost" onClick={onClose} className="px-2 py-1 text-sm">
              Close
            </Button>
          </div>
        )}
        <div className="p-4 overflow-y-auto" style={{ maxHeight: contentMaxHeight }}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
